import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { NavColumn } from '@/data/navigation'

interface Props {
  label: string
  columns: NavColumn[]
  /** Optional "view all" style link rendered in the footer band. */
  footer?: { label: string; href: string }
}

/**
 * Desktop mega menu for the dark sticky header: a wide, multi-column panel for
 * groups with too many links for a single dropdown column (Solutions). Same
 * open/close/hover/keyboard behavior as NavDropdown and the same
 * docs/conventions/frontend.md §6 rules (Link navigation, `pt-2` hover bridge,
 * explicit light text on the dark surface). Left-aligned to the trigger because
 * Solutions is the leftmost nav item, so the wide panel stays on-screen.
 */
export function NavMegaMenu({ label, columns, footer }: Props) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()

  // Close when the route changes (a click on any item navigates).
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  // Close on outside click and Escape while open.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const active = columns.some((column) =>
    column.items.some((item) => pathname === item.href || pathname.startsWith(`${item.href}/`))
  )

  return (
    <div ref={containerRef} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'flex items-center gap-1 text-sm transition',
          active || open ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
        )}
      >
        {label}
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-200', open && 'rotate-180')} />
      </button>

      {/* pt-2 (padding, not margin) keeps the cursor inside the hover area in transit */}
      <div className={cn('absolute top-full left-0 z-50 pt-2', !open && 'hidden')}>
        <div className="w-[46rem] rounded-xl border border-gray-800 bg-gray-950 p-4 shadow-xl shadow-black/50">
          <div className="grid grid-cols-4 gap-x-4 gap-y-6">
            {columns.map((column) => (
              <div key={column.heading}>
                <p className="mb-2 px-2 text-xs font-semibold tracking-widest text-gray-500 uppercase">
                  {column.heading}
                </p>
                <div className="space-y-0.5">
                  {column.items.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block rounded-lg px-2 py-2 transition hover:bg-gray-900"
                    >
                      <span className="block text-sm font-medium text-gray-100">{item.label}</span>
                      {item.description && (
                        <span className="mt-0.5 block text-xs leading-snug text-gray-400">{item.description}</span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {footer && (
            <Link
              to={footer.href}
              className="mt-4 block rounded-lg border-t border-gray-800 px-2 pt-3 pb-1 text-sm font-medium text-gray-300 transition hover:text-gray-100"
            >
              {footer.label}
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
